import { useState } from 'react'
import type { Ref } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Input } from '../Input'
import type { PasswordInputProps } from './PasswordInput.types'
import './PasswordInput.scss'

/**
 * PasswordInput Component
 *
 * Wraps the shared Input component with an eye/eye-off visibility toggle.
 * Each instance manages its own visibility state independently.
 *
 * @example
 * // Standalone (default) — renders its own bordered container
 * <PasswordInput placeholder="Password" value={pw} onChange={e => setPw(e.target.value)} />
 *
 * @example
 * // Inline — use inside a page's __field div
 * <div className="login-page__field">
 *   <Lock size={20} />
 *   <PasswordInput variant="inline" placeholder="Password" value={pw} onChange={e => setPw(e.target.value)} />
 * </div>
 */
export function PasswordInput({
  variant = 'standalone',
  error = false,
  disabled = false,
  className = '',
  ref,
  ...props
}: PasswordInputProps & { ref?: Ref<HTMLInputElement> }) {
  const [isVisible, setIsVisible] = useState(false)

  const toggleClassName = `password-input__toggle${variant === 'inline' ? ' password-input__toggle--inline' : ''}`

  const toggleButton = (
    <button
      type="button"
      className={toggleClassName}
      onClick={() => setIsVisible(prev => !prev)}
      aria-label={isVisible ? 'Hide password' : 'Show password'}
      tabIndex={-1}
      disabled={disabled}
    >
      {isVisible ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  )

  if (variant === 'inline') {
    return (
      <>
        <Input
          ref={ref}
          type={isVisible ? 'text' : 'password'}
          error={error}
          disabled={disabled}
          className={`password-input__input--inline ${className}`.trim()}
          {...props}
        />
        {toggleButton}
      </>
    )
  }

  return (
    <div className="password-input">
      <Input
        ref={ref}
        type={isVisible ? 'text' : 'password'}
        error={error}
        disabled={disabled}
        className={`password-input__input ${className}`.trim()}
        {...props}
      />
      {toggleButton}
    </div>
  )
}
